import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Drawer, Button, Row, Col, Pagination, Spin } from 'antd';

import debounce from 'lodash/debounce';
import moment from 'moment';

import TradeItem from '../../../components/TradeItem/TradeItem';
import SelectDropdown from '../../../components/SelectDropdown/SelectDropdown';
import { Filter, CloseIcon, ChevronLeft, ChevronRight } from '../../../assets/icons';

import { getTradeConfirmation, resetTradeConfirmations } from '../../../store/reducer/accountStatementSlice';

import { formatCurrency } from '../../../utils/usCurrencyFormatter';

const DURATIONS = ['Last 30 days', 'Last 60 days', 'Last 90 days', 'All time'];
const TRADE_TYPES = ['Buy', 'Sell', 'All'];

const TradeDocuments = () => {
  const [taxDrawer, setTaxDrawer] = useState(false);

  const [selectedDuration, setSelectedDuration] = useState('Last 30 days');
  const [selectedTradeType, setSelectedTradeType] = useState('All');

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);

  const dispatch = useDispatch();
  const { tradingConfirmationList } = useSelector((state) => state.accountStatements);

  const [isLoading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    duration: selectedDuration,
    tradeType: selectedTradeType,
  });

  const getStartEndDate = (duration) => {
    switch (duration) {
      case 'Last 30 days':
        return { startDate: moment().startOf('day').add(-30, 'days').toISOString(), endDate: moment().endOf('day').toISOString() };

      case 'Last 60 days':
        return { startDate: moment().startOf('day').add(-60, 'days').toISOString(), endDate: moment().endOf('day').toISOString() };

      case 'Last 90 days':
        return { startDate: moment().startOf('day').add(-90, 'days').toISOString(), endDate: moment().endOf('day').toISOString() };

      default:
        return null;
    }
  };

  async function fetchTradeConfirmations() {
    const dates = getStartEndDate(filters.duration);
    try {
      setLoading(true);
      await dispatch(
        getTradeConfirmation({
          page: currentPage,
          limit: perPage,
          startDate: dates?.startDate ?? null,
          endDate: dates?.endDate ?? null,
          tradeType: filters.tradeType === 'All' ? '' : filters.tradeType,
        })
      );
    } catch (error) {
      // eslint-disable-next-line
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onFetchDataDebounced = debounce(fetchTradeConfirmations, 0);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, perPage, filters.duration, filters.tradeType]);

  useEffect(() => {
    return () => {
      dispatch(resetTradeConfirmations());
    };
  }, []);

  return (
    <>
      <div className='data__header'>
        <h4 className='mb-8'>Trade Confirmations</h4>
        <p className='cursor-pointer filter-trigger' onClick={() => setTaxDrawer(true)}>
          <Filter verticalAlign='-7px' /> <span className='fw-600'>Filters</span> <span>(1)</span>{' '}
        </p>
        <p className='data__header__description'>All trade confirmations will be displayed here once they become available.</p>
      </div>

      <div className='data__filters'>
        <Form>
          <Row gutter={16}>
            <Col span={8}>
              <SelectDropdown
                initialValue={selectedDuration}
                options={DURATIONS}
                classVal={'filter-select'}
                floatClassVal={'filter-float'}
                label={'Time Period'}
                id={'time-input'}
                inputId={'timePeriod'}
                name={'timePeriod'}
                onChangeSelect={(_, value) => {
                  setSelectedDuration(value);
                  setFilters({ ...filters, duration: value });
                }}
                alwaysSmall
              />
            </Col>
            <Col span={8}>
              <SelectDropdown
                initialValue={selectedTradeType}
                options={TRADE_TYPES}
                classVal={'filter-select'}
                floatClassVal={'filter-float'}
                label={'Trade Type'}
                id={'tradeType'}
                inputId={'tradeType'}
                name={'tradeType'}
                onChangeSelect={(_, value) => {
                  setSelectedTradeType(value);
                  setFilters({ ...filters, tradeType: value });
                }}
                alwaysSmall
              />
            </Col>
          </Row>
        </Form>
      </div>
      <div className='data__items mb-24'>
        <Spin spinning={isLoading}>
          {tradingConfirmationList?.tradingConfirmationData?.length > 0 ? (
            <>
              <div className='data__items__head'>
                <p className='subhead-3'>Date</p>
                <p className='subhead-3'>Trade</p>
                <p className='subhead-3'>Principal amount</p>
              </div>
              {tradingConfirmationList?.tradingConfirmationData?.map((data, index) => {
                return (
                  <TradeItem
                    key={`${index + 1}`}
                    date={data?.date && moment(data?.date).format('MMM DD, YYYY')}
                    trade={data?.trade}
                    amount={formatCurrency(data?.principleAmount ?? 0)}
                    actionURL={data?.downloadUrl}
                  />
                );
              })}
            </>
          ) : (
            <>
              {!isLoading && !tradingConfirmationList?.tradingConfirmationData?.length && (
                <div className='nodata'>
                  <p className='subhead-3 mb-4'>No trade confirmations found.</p>
                  <p className='mb-0'>All trade confirmations will appear here once available.</p>
                </div>
              )}
            </>
          )}
        </Spin>
      </div>
      {!!tradingConfirmationList?.totalCount && (
        <Pagination
          className='trade-pagination'
          prevIcon={<ChevronLeft color='#726460' size={20} verticalAlign='-5px' />}
          nextIcon={<ChevronRight color='#726460' size={20} verticalAlign='-5px' />}
          current={currentPage}
          pageSize={perPage}
          total={tradingConfirmationList?.totalCount}
          showLessItems
          showTotal={(total, range) => !!tradingConfirmationList?.totalCount && `${range[0]}-${range[1]} of ${total} results`}
          onChange={(page) => {
            setCurrentPage(page);
          }}
        />
      )}

      {taxDrawer && (
        <Drawer
          className='filter-drawer'
          open={taxDrawer}
          footer={false}
          onClose={() => setTaxDrawer(false)}
          title={<p className='mb-0 subhead-3'>Filter Account Statements</p>}
          closeIcon={<CloseIcon />}
          width='100%'
        >
          <Form className='filter-form'>
            <div className='filter-fields'>
              <SelectDropdown
                initialValue={selectedDuration}
                options={DURATIONS}
                classVal={'filter-select'}
                floatClassVal={'filter-float'}
                label={'Time Period'}
                id={'time-input'}
                inputId={'timePeriod'}
                name={'timePeriod'}
                onChangeSelect={(_, value) => setSelectedDuration(value)}
                alwaysSmall
              />
              <SelectDropdown
                initialValue={selectedTradeType}
                options={TRADE_TYPES}
                classVal={'filter-select'}
                floatClassVal={'filter-float'}
                label={'Trade Type'}
                id={'tradeType'}
                inputId={'tradeType'}
                name={'tradeType'}
                onChangeSelect={(_, value) => setSelectedTradeType(value)}
                alwaysSmall
              />
            </div>
            <Button
              type='primary'
              className='w-100'
              onClick={() => {
                setFilters({ tradeType: selectedTradeType, duration: selectedDuration });
                setTaxDrawer(false);
              }}
            >
              Apply Filters
            </Button>
          </Form>
        </Drawer>
      )}
    </>
  );
};

export default TradeDocuments;
